/**
 * Outsourcing (the coating desk): every write, ONE copy for the desktop page (pages/CoatingManagementPage.jsx,
 * office and the Shipping hub's operator mode) and the phone Shipping screen's Outsourcing tab
 * (mobile/screens/shipping/OutsourcingTab.jsx). Moved from CoatingManagementPage word for word (2026-10-29,
 * docs/mobile-pwa-plan.md "One copy of every action"); the page's tests pin the words and requests.
 *
 * `cui` (how an action talks to the person; build it with desktopCoatingUi / phoneCoatingUi):
 *   alert(text), confirm(text)   the desktop's window.alert / confirm; the phone's full-screen panels (awaited).
 *   prompt(text)                 a typed answer (a manager's reason); absent = window.prompt (the desktop).
 *   actor(why)                   who is doing it: {} (the office desk: nobody), {pin} (the Working-as session's
 *                                token), null = the person backed out (nothing is written).
 *   receivePin(opo)              the receive's PIN (the desk types one; the hub's Working-as session answers).
 *   workingPin(message) | null   the pallet checklists' "who" (the Working-as session); null = a typed PIN.
 *   printBlob(blob) | null       the desktop's print dialog. A phone has none: the paper comes back as a
 *                                document to offer, and pallet labels go through the shop print queue.
 *   labelStation                 the phone's label station id (features/print/labelStation).
 *
 * Every action takes `setBusy(on)` (around the request) and `onDone(data)` (the page's follow-up, run inside the
 * busy window, as the inline handlers did) and resolves {done, ...}.
 */
import pinPrompt from '../../utils/pinPrompt';
import openPdfInPrintDialog from '../../utils/openPdfInPrintDialog';
import { offerReattach, sendWarningsText, sendWithChecklist } from '../pallets/opoPalletFlows';
import { sendSignNote } from '../signing/vendorSigning';
import { printLabelsAt } from '../salesOrders/palletActions';
import { fmtQty, palletLabelsPath, paperDocument, parseServerError, receiveLinesFrom } from './coatingData';
import { emailSenderHeaders } from '../../utils/emailSender';

const noop = () => {};

/** Who is doing it, from a Working-as ui (`ui.identity(why)` → token | null): {pin} | null. */
export function identityActor(ui) {
  return async (why) => {
    const pin = ui && ui.identity ? await ui.identity(why) : null;
    return pin ? { pin } : null;
  };
}

/**
 * The desktop page's `cui`. The office desk (operator false) names nobody and types the receive PIN; the Shipping
 * hub (operator true, `ui` = workingAsUi(desktopUi)) answers every "who" with the Working-as session.
 */
export function desktopCoatingUi({ operator = false, ui = null } = {}) {
  return {
    alert: (text) => window.alert(text),
    confirm: (text) => window.confirm(text),
    actor: operator ? identityActor(ui) : async () => ({}),
    receivePin: operator
      ? (opo) => (ui && ui.identity ? ui.identity('receive') : null)
      : (opo) => pinPrompt(`Receiving ${opo.opo_number} — enter your PIN:`),
    workingPin: operator ? (m) => ui.pin(m) : null,
    printBlob: (blob) => openPdfInPrintDialog(blob),
  };
}

/** The phone's `cui` (the phone Shipping screen's ui: phoneUi + Working-as + its label station). Always the
 *  floor's (operator) rules; never the office's. */
export function phoneCoatingUi(ui) {
  return {
    alert: (text) => ui.alert(text),
    confirm: (text) => ui.confirm(text),
    prompt: (text) => ui.prompt(text),
    actor: identityActor(ui),
    receivePin: () => (ui.identity ? ui.identity('receive') : null),
    workingPin: (m) => ui.pin(m),
    printBlob: null,
    labelStation: ui.labelStation,
  };
}

/** The create/add answer's sentence, with the pallet warnings (what moved: extras left in To send, another
 *  order's pieces riding the pallet added). */
function withPalletWarnings(msg, data) {
  if ((data.pallet_warnings || []).length) return `${msg}\n\nHeads up:\n${data.pallet_warnings.join('\n')}`;
  return msg;
}

/** Make a paper from the ticked tasks (task mode: the paper is the pallets they are packed on). */
export async function createManifest({ apiBase, cui, taskIds, vendorId, dueBack = '', notes = '',
  setBusy = noop, onDone = noop }) {
  if (!taskIds || !taskIds.length) return { done: false };
  if (!vendorId) {
    await cui.alert('Pick the coating vendor for this manifest.');
    return { done: false };
  }
  const who = await cui.actor('create');
  if (!who) return { done: false };
  const body = { task_ids: [...taskIds], vendor_id: Number(vendorId), ...who };
  if (dueBack) body.due_back = dueBack;
  if (notes.trim()) body.notes_to_vendor = notes.trim();
  setBusy(true);
  try {
    const res = await fetch(`${apiBase}/coating/manifests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    const data = await res.json();
    await cui.alert(withPalletWarnings(data.message || `Coating manifest ${data.opo_number} created.`, data));
    await onDone(data);
    return { done: true, data };
  } catch (err) {
    await cui.alert(err.message || 'Failed to create the manifest.');
    return { done: false };
  } finally {
    setBusy(false);
  }
}

/** One paper for one pallet, exactly what the packing plan puts on it (pallet mode). */
export async function createPalletManifest({ apiBase, cui, palletShortId, setBusy = noop, onDone = noop }) {
  if (!(await cui.confirm(
    `Create a coating manifest for pallet ${palletShortId}?\n\nIt will carry exactly the quantities ` +
    `the packaging plan puts on that pallet — a job split across two pallets goes partly on each ` +
    `pallet's paper and its coating task completes when both papers are received.`
  ))) return { done: false };
  const who = await cui.actor('create');
  if (!who) return { done: false };
  setBusy(true);
  try {
    const res = await fetch(`${apiBase}/coating/manifests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pallet_short_id: palletShortId, ...who }),
    });
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    const data = await res.json();
    await cui.alert(withPalletWarnings(data.message || `Manifest ${data.opo_number} created.`, data));
    await onDone(data);
    return { done: true, data };
  } catch (err) {
    await cui.alert(err.message || 'Failed to create the pallet manifest.');
    return { done: false };
  } finally {
    setBusy(false);
  }
}

/** Add the ticked tasks to a Draft paper of the same order. */
export async function addToDraft({ apiBase, cui, target, taskIds, setBusy = noop, onDone = noop }) {
  if (!target || !taskIds || !taskIds.length) return { done: false };
  const who = await cui.actor('edit');
  if (!who) return { done: false };
  setBusy(true);
  try {
    const res = await fetch(`${apiBase}/coating/manifests/${target.opo_id}/lines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_ids: [...taskIds], ...who }),
    });
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    const data = await res.json().catch(() => ({}));
    await cui.alert(withPalletWarnings(`Added ${taskIds.length} line(s) to ${target.opo_number}.`, data));
    await onDone(data);
    return { done: true, data };
  } catch (err) {
    await cui.alert(err.message || 'Failed to add lines to the draft.');
    return { done: false };
  } finally {
    setBusy(false);
  }
}

/** Take a line off a Draft paper: its task returns to staging. */
export async function removeLine({ apiBase, cui, opo, line, setBusy = noop, onDone = noop }) {
  const label = line.description || line.part_product_id || `line ${line.line_number}`;
  if (!(await cui.confirm(`Remove ${label} from ${opo.opo_number}? The task returns to staging.`))) return { done: false };
  const who = await cui.actor('edit');
  if (!who) return { done: false };
  setBusy(true);
  try {
    // who removed it rides in a JSON body (never a PIN in the query string); the office page sends none
    const res = await fetch(
      `${apiBase}/coating/manifests/${opo.opo_id}/lines/${line.opo_line_id}`,
      who.pin
        ? { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(who) }
        : { method: 'DELETE' }
    );
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    await onDone();
    return { done: true };
  } catch (err) {
    await cui.alert(err.message || 'Failed to remove the line.');
    return { done: false };
  } finally {
    setBusy(false);
  }
}

/**
 * Send a paper: the re-check, the pallet checklist and the send (features/pallets/opoPalletFlows sendWithChecklist),
 * then what the API changed, then the paper to print (the desktop's print dialog; a phone gets `paper` back to
 * offer), then (office only, `offerEmail`) the vendor email.
 *   operator         the floor's rules: who sends is the Working-as session, the checklist must be read (strict).
 *   allowUnchecked   operator only: asked when the checklist can't be read (the hub's Office tools); null = never.
 *   reload()         re-read the board after a paper that was fixed and then not sent.
 * Resolves {sent, paper?}.
 */
export async function sendManifest({ apiBase, cui, opo, operator = false, allowUnchecked = null, offerEmail = false,
  reload = noop, setBusy = noop, onDone = noop }) {
  const vendorLabel = opo.vendor_name || 'the vendor';
  // The pallet checklist (2026-10-26): when the paper names pallets, every pallet is checked off
  // (beacon pulled + docked, 4x6 label on) and the send records it in the same request.
  const sent = await sendWithChecklist({
    apiBase, opo, parseError: parseServerError,
    confirmPlain: (text) => cui.confirm(text), alert: (text) => cui.alert(text),
    // who fixes a paper that no longer matches its pallets (the hub's Working-as; the desk: nobody)
    identity: () => cui.actor('edit'),
    // a manager's reason for "Send as drafted": the phone's own prompt (the desktop keeps window.prompt)
    ...(cui.prompt ? { askReason: (text) => cui.prompt(text) } : {}),
    // the hub: who sends it is the Working-as session; the checklist must be read (Office tools may skip it)
    ...(operator ? { pin: (m) => cui.workingPin(m), strict: true, allowUnchecked } : {}),
  });
  if (!sent.sent) {
    if (sent.fixed) await reload();   // the Draft was rewritten, then not sent: show its new lines
    return { sent: false };
  }
  let paper = null;
  setBusy(true);
  try {
    const data = sent.data || {};
    const notes = sendWarningsText(data);
    if (notes) await cui.alert(notes);
    // the vendor's signing code (only an API with vendor signing on answers these keys)
    const signNote = sendSignNote(data);
    if (signNote) await cui.alert(signNote);
    if (data.recheck && data.recheck.applied) {
      await cui.alert(`The paper was fixed before it was sent: ${data.recheck.text}.`);
    }
    if (Array.isArray(data.quantity_adjustments) && data.quantity_adjustments.length) {
      await cui.alert(
        'Line quantities were refreshed from the live tasks before sending ' +
          '(defects recorded since batching):\n' +
          data.quantity_adjustments
            .map((a) => `• line ${a.opo_line_id}: ${fmtQty(a.old_quantity_sent)} → ${fmtQty(a.new_quantity_sent)}`)
            .join('\n')
      );
    }
    // Print the paper that travels with the parts.
    if (cui.printBlob) {
      try {
        const pdfRes = await fetch(`${apiBase}/outside_processing_orders/${opo.opo_id}/pdf`);
        if (pdfRes.ok) {
          cui.printBlob(await pdfRes.blob());
        } else {
          await cui.alert('Manifest sent, but the PDF failed to load for printing. Use Reprint on the At Vendor tab.');
        }
      } catch (err) {
        console.error('Failed to print manifest PDF:', err);
        await cui.alert('Manifest sent, but printing failed. Use Reprint on the At Vendor tab.');
      }
    } else {
      paper = paperDocument(apiBase, opo);
    }
    if (offerEmail && (await cui.confirm(`Email the manifest to ${vendorLabel}?`))) {
      try {
        const emailRes = await fetch(`${apiBase}/outside_processing_orders/${opo.opo_id}/email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...emailSenderHeaders() },
          body: JSON.stringify({}),
        });
        if (!emailRes.ok) {
          await cui.alert(await parseServerError(emailRes));
        } else {
          const eb = await emailRes.json().catch(() => ({}));
          await cui.alert(eb.message || 'Manifest emailed.');
        }
      } catch (err) {
        await cui.alert(err.message || 'Emailing the manifest failed.');
      }
    }
    await onDone(data);
    return { sent: true, paper };
  } catch (err) {
    await cui.alert(err.message || 'Failed to send the manifest.');
    return { sent: true, paper };
  } finally {
    setBusy(false);
  }
}

/**
 * One 4x6 label per pallet on the manifest (jobq-api pallet_labels.pdf: each pallet lists only this order's
 * pieces). Fetched first (as a blob, so the API key rides on the request): a 409 (no line on a pallet) shows the
 * API's sentence, and pieces on no pallet get no label (X-Loose-Units / X-Loose-Detail), said before printing.
 * The desktop prints the blob; a phone queues the same PDF at its label station (the shop print queue).
 * Resolves {done, message?} (`message` = where a phone's labels went).
 */
export async function printPalletLabels({ apiBase, cui, opo }) {
  try {
    const res = await fetch(`${apiBase}${palletLabelsPath(opo)}`);
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    const loose = Number(res.headers?.get?.('X-Loose-Units') || 0);
    if (loose > 0) {
      const detail = res.headers.get('X-Loose-Detail') || '';
      await cui.alert(
        `${fmtQty(loose)} pcs on ${opo.opo_number} are on no pallet in the packing plan, ` +
          `so they get no label${detail ? ` (${detail})` : ''}. They travel with the paper.`
      );
    }
    if (cui.printBlob) {
      cui.printBlob(await res.blob());
      return { done: true };
    }
    const queued = await printLabelsAt({
      station: cui.labelStation, path: palletLabelsPath(opo), kind: 'opo_pallet_labels',
      what: `${opo.opo_number} pallet labels`, trigger: 'phone_outsourcing',
    });
    return { done: true, message: queued.message };
  } catch (err) {
    await cui.alert(err.message || 'Failed to load the pallet labels.');
    return { done: false };
  }
}

/** Print the paper again (the desktop's print dialog). */
export async function reprintPaper({ apiBase, cui, opo }) {
  try {
    const res = await fetch(`${apiBase}/outside_processing_orders/${opo.opo_id}/pdf`);
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    if (!cui.printBlob) return { done: true, paper: paperDocument(apiBase, opo) }; // a phone: offer it instead
    cui.printBlob(await res.blob());
    return { done: true };
  } catch (err) {
    await cui.alert(err.message || 'Failed to load the manifest PDF.');
    return { done: false };
  }
}

/**
 * Receive pieces back: the counts per line (`rows`, receiveRowsFor's shape), who received it, and (the office only)
 * the vendor invoice # / cost / freight; left out of the request when `office` is false (cost would go in as the
 * estimate). Then the pallets now back with a beacon to put on (nothing when none).
 */
export async function receiveManifest({ apiBase, cui, opo, rows, office = false, invoice = '', cost = '', freight = '',
  setBusy = noop, onDone = noop }) {
  const parsed = receiveLinesFrom(opo, rows);
  if (parsed.error) {
    await cui.alert(parsed.error);
    return { done: false };
  }
  const pin = await cui.receivePin(opo);
  if (!pin) return { done: false };
  const body = { pin, lines: parsed.lines };
  // the invoice fields are the office's: hidden on the floor, and then left out (cost would go in as the estimate)
  if (office) {
    if (invoice.trim()) body.vendor_invoice_number = invoice.trim();
    if (cost !== '' && !isNaN(parseFloat(cost))) body.actual_cost = parseFloat(cost);
    if (freight !== '' && !isNaN(parseFloat(freight))) body.freight_cost = parseFloat(freight);
  }
  setBusy(true);
  try {
    const res = await fetch(`${apiBase}/coating/manifests/${opo.opo_id}/receive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      await cui.alert(await parseServerError(res));
      return { done: false };
    }
    const data = await res.json();
    const done = (data.completed_task_ids || []).length;
    const partial = (data.partially_received_task_ids || []).length;
    if (done || partial) {
      await cui.alert(`${done} task(s) completed, ${partial} partially received.`);
    } else {
      await cui.alert('Receipt recorded.');
    }
    await onDone(data);
    // pallets now back with a beacon to put on (2026-10-26): the list, nothing when none
    if (cui.workingPin) await offerReattach(opo.opo_id, { pin: cui.workingPin });
    else await offerReattach(opo.opo_id);
    return { done: true, data };
  } catch (err) {
    await cui.alert(err.message || 'Failed to receive the manifest.');
    return { done: false };
  } finally {
    setBusy(false);
  }
}

/** Beacons back on (At vendor): the pallets back from the vendor, their beacons lit, recorded back on. */
export async function beaconsBackOn({ cui, opo }) {
  const r = await offerReattach(opo.opo_id, cui.workingPin
    ? { always: true, pin: cui.workingPin } : { always: true });
  if (!r.shown && r.message) await cui.alert(r.message);
  return r;
}

/**
 * One pallet flow (send / find / beacons back on) at a time: the checklist reads the API before anything shows,
 * and a second click in that moment opened a second send (review 2026-10-26). `run(fn)` does nothing while one runs.
 */
export function createFlowGuard() {
  let running = false;
  return async function run(fn) {
    if (running) return undefined;
    running = true;
    try {
      return await fn();
    } finally {
      running = false;
    }
  };
}
